/**
 * Aspect-ratio-locked image slot for a book page.
 *
 * The slot always reserves space for the final image using the known aspect
 * ratio, so swapping the shimmering placeholder for the real illustration
 * causes zero layout shift (CLS = 0).
 *
 * Three placeholder states cover the level-scaled illustration cadence:
 *   - generating  → friendly "drawing your picture" shimmer.
 *   - missing      → a calm "picture coming soon" card (an expected image that
 *     failed or has not been backfilled yet); a grown-up can fix it later.
 *   - text-only    → no slot at all (the page is intentionally words-only), so
 *     the reader never sees a misleading spinner for a page with no picture.
 */

interface ImagePlaceholderProps {
  /** width / height of the eventual image; 1 until known. */
  aspectRatio: number;
  /** Final image URL, or null while still generating / missing. */
  imageUrl: string | null;
  /** Alt text describing the page illustration. */
  alt: string;
  /** Whether this page is meant to carry an illustration. Defaults to true. */
  expectsImage?: boolean;
  /** Whether the book is still streaming (so a missing image is still drawing). */
  generating?: boolean;
}

export function ImagePlaceholder({
  aspectRatio,
  imageUrl,
  alt,
  expectsImage = true,
  generating = false,
}: ImagePlaceholderProps) {
  const ratio = aspectRatio > 0 ? aspectRatio : 1;

  // Intentionally text-only page: render no image slot at all.
  if (!imageUrl && !expectsImage) {
    return null;
  }

  return (
    <div
      className="kid-card"
      style={{
        aspectRatio: String(ratio),
        width: "100%",
        overflow: "hidden",
        position: "relative",
      }}
    >
      {imageUrl ? (
        <img
          src={imageUrl}
          alt={alt}
          loading="lazy"
          decoding="async"
          style={{
            width: "100%",
            height: "100%",
            objectFit: "cover",
            display: "block",
          }}
        />
      ) : generating ? (
        <div
          className="shimmer"
          role="status"
          aria-live="polite"
          style={{
            width: "100%",
            height: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <span style={{ fontSize: "1.25rem", fontWeight: 700 }}>
            ✨ Drawing your picture…
          </span>
        </div>
      ) : (
        <div
          role="img"
          aria-label="Picture coming soon"
          style={{
            width: "100%",
            height: "100%",
            display: "flex",
            flexDirection: "column",
            gap: "0.5rem",
            alignItems: "center",
            justifyContent: "center",
            textAlign: "center",
            padding: "1rem",
          }}
        >
          <span style={{ fontSize: "2rem" }} aria-hidden="true">
            🎨
          </span>
          <span style={{ fontSize: "1.1rem", fontWeight: 700 }}>
            Picture coming soon
          </span>
        </div>
      )}
    </div>
  );
}
